import React, { useState } from 'react'
import { useMutation, useQuery, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import { editSection, restaurantList, createSection, getSections } from '../../apollo'
import {
  Box, Typography, TextField, Button, Alert, CircularProgress,
  Switch, FormControlLabel, Checkbox, Divider
} from '@mui/material'
import StorefrontIcon from '@mui/icons-material/Storefront'

const CREATE_SECTION = gql`${createSection}`
const EDIT_SECTION = gql`${editSection}`
const GET_SECTIONS = gql`${getSections}`
const GET_RESTAURANT = gql`${restaurantList}`

function Section(props) {
  const isEdit = !!props.section
  const mutation = isEdit ? EDIT_SECTION : CREATE_SECTION

  const [name, setName] = useState(props.section?.name || '')
  const [nameError, setNameError] = useState('')
  const [enabled, setEnabled] = useState(props.section ? props.section.enabled : true)
  const [selectedRestaurants, setSelectedRestaurants] = useState(
    props.section ? (props.section.restaurants ?? []).map(r => (typeof r === 'object' && r !== null) ? r._id : r) : []
  )
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')

  const { data, loading: loadingRestaurants } = useQuery(GET_RESTAURANT)
  const allRestaurants = data?.restaurantList ?? []

  const [mutate, { loading }] = useMutation(mutation, {
    refetchQueries: [{ query: GET_SECTIONS }],
    onCompleted: () => {
      setSuccess(isEdit ? 'Section updated successfully!' : 'Section created successfully!')
      setError('')
      setTimeout(() => { if (typeof props.onClose === 'function') props.onClose() }, 1500)
    },
    onError: err => {
      setError(err.graphQLErrors?.[0]?.message || err.message || 'Something went wrong')
      setSuccess('')
    }
  })

  const toggleRestaurant = id => {
    setSelectedRestaurants(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    )
  }

  const handleSubmit = () => {
    if (!name.trim()) { setNameError('Section name is required'); return }
    setNameError('')
    mutate({
      variables: {
        section: {
          _id: isEdit ? props.section._id : '',
          name: name.trim(),
          enabled,
          restaurants: selectedRestaurants
        }
      }
    })
  }

  return (
    <Box>
      {/* Section Name */}
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', mb: 0.6 }}>Section Name</Typography>
        <TextField
          fullWidth size="small"
          placeholder="e.g. Fast Food, Top Picks..."
          value={name}
          onChange={e => setName(e.target.value)}
          error={!!nameError}
          helperText={nameError}
          autoFocus
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
        />
      </Box>

      {/* Enable toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, p: 1.5, borderRadius: '10px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
        <Box>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>Enable section</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>Show this section in the customer app</Typography>
        </Box>
        <Switch
          checked={enabled}
          onChange={e => setEnabled(e.target.checked)}
          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#7c3aed' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#7c3aed' } }}
        />
      </Box>

      {/* Assign Restaurants */}
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', mb: 0.8 }}>
          Assign Restaurants{' '}
          <Typography component="span" sx={{ color: '#7c3aed', fontWeight: 700 }}>({selectedRestaurants.length} selected)</Typography>
        </Typography>
        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden', maxHeight: 220, overflowY: 'auto' }}>
          {loadingRestaurants ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={20} /></Box>
          ) : allRestaurants.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <StorefrontIcon sx={{ fontSize: 32, color: '#e2e8f0', mb: 0.5 }} />
              <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8' }}>No restaurants available</Typography>
            </Box>
          ) : allRestaurants.map((r, i) => (
            <Box key={r._id}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedRestaurants.includes(r._id)}
                    onChange={() => toggleRestaurant(r._id)}
                    size="small"
                    sx={{ color: '#cbd5e1', '&.Mui-checked': { color: '#7c3aed' } }}
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>{r.name}</Typography>
                    {r.address && <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>{r.address}</Typography>}
                  </Box>
                }
                sx={{ width: '100%', mx: 0, px: 1.5, py: 0.8, '&:hover': { bgcolor: '#f8fafc' } }}
              />
              {i < allRestaurants.length - 1 && <Divider />}
            </Box>
          ))}
        </Box>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '10px', fontSize: '0.82rem' }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px', fontSize: '0.82rem' }}>{error}</Alert>}

      {/* Buttons */}
      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
        <Button
          onClick={() => props.onClose?.()}
          sx={{ borderRadius: '10px', textTransform: 'none', color: '#64748b', fontWeight: 600, px: 2.5 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 3, bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}>
          {loading ? 'Saving...' : isEdit ? 'Update Section' : 'Create Section'}
        </Button>
      </Box>
    </Box>
  )
}

export default withTranslation()(Section)